/**
 * @file index.tsx
 *
 * @fileoverview Renders statistics on a dark background.
 */
import React from 'react';
import { Text } from '~/vcl/atoms/13-text/03-text';
import styles from './statsFadingBackground.module.scss';
import { StatsWithFadingBackgroundParagraph } from '~/jobiqo/types/types';
import dynamic from 'next/dynamic';
import LazyHydrate from 'react-lazy-hydration';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import Main from '~/vcl/atoms/06-layout/09-main';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { cva } from 'class-variance-authority';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const LandingPageIcon = dynamic(
  () => import('~/jobiqo/components/Landingpage/LandingPageIcon'),
  {
    ssr: false,
  },
);

const fullWrapper = cva([styles.fullWrapper], {
  variants: {
    hasBg: {
      true: styles.fullWrapperBg,
    },
  },
});

interface StatsWithFadingBackgroundProps {
  row: StatsWithFadingBackgroundParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}
export const StatsWithFadingBackground = ({
  row,
  maxContainerSize,
}: StatsWithFadingBackgroundProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);

  return (
    <LazyHydrate whenIdle={true}>
      <div
        className={fullWrapper({
          hasBg: frontendTheme.LANDING_PAGES.STATS_FADING_BG.SHOW_BG_OVERLAY,
        })}
      >
        <HideMobile>
          <div className={styles.absoluteContainer}>
            <Grid
              columns={[1, 2]}
              gap={0}
              className={`vcl-full-height ${
                frontendTheme.LANDING_PAGES.STATS_FADING_BG.CELL_1_TOP
                  ? styles.grid
                  : ''
              }`}
            >
              <Cell />
              <Cell>
                <div
                  className={`${
                    frontendTheme.LANDING_PAGES.STATS_FADING_BG.SHOW_BG_OVERLAY
                      ? styles.bgWrapper
                      : ''
                  }`}
                >
                  <img
                    className={styles.sideImage}
                    width="1920"
                    height="1080"
                    loading="lazy"
                    src={row.image.url}
                    alt={row.image.alt ?? ''}
                    {...(row.image.title && { title: row.image.title })}
                    style={{
                      opacity:
                        frontendTheme.LANDING_PAGES.STATS_FADING_BG
                          .SIDE_IMAGE_OPACITY,
                      objectFit:
                        frontendTheme.LANDING_PAGES.STATS_FADING_BG
                          .SIDE_IMAGE_OBJECT_FIT,
                    }}
                  />
                  {frontendTheme.LANDING_PAGES.STATS_FADING_BG
                    .SHOW_GRADIENT && <div className={styles.gradient} />}
                </div>
              </Cell>
            </Grid>
          </div>
        </HideMobile>
        <Spacing
          marginTop={0}
          marginBottom={0}
          paddingTop={row.spacing ?? 12}
          paddingBottom={row.spacing ?? 12}
        >
          <div className={styles.content}>
            <Main size="xLarge">
              <Grid columns={[1, 2]} gap={0}>
                <Cell display="grid" justifySelf="end">
                  <Spacing marginTop={0}>
                    <Main
                      {...(maxContainerSize
                        ? { size: maxContainerSize }
                        : null)}
                    >
                      <FlexItems
                        flexDirection={
                          frontendTheme.LANDING_PAGES.STATS_FADING_BG
                            .SUBTITLE_ABOVE_TITLE
                            ? 'column'
                            : 'column-reverse'
                        }
                      >
                        {row.statsSubtitle && (
                          <Text
                            color={
                              frontendTheme.LANDING_PAGES.STATS_FADING_BG
                                .SUBTITLE_COLOR ?? (row.color as any)
                            }
                            fontWeight="bold"
                            textTransform={
                              frontendTheme.LANDING_PAGES.STATS_FADING_BG
                                .SUBTITLE_TEXT_TRANSFORM
                            }
                          >
                            {row.statsSubtitle}
                          </Text>
                        )}
                        <Spacing
                          size={
                            frontendTheme.LANDING_PAGES.STATS_FADING_BG
                              .SUBTITLE_TITLE_SPACING
                          }
                        />
                        <Text
                          color={
                            frontendTheme.LANDING_PAGES.STATS_FADING_BG
                              .TITLE_COLOR as any
                          }
                          tag="h2"
                          fontSize={[5, 7]}
                          lineHeight={[5, 7]}
                        >
                          {row.title}
                        </Text>
                      </FlexItems>
                      <Spacing size="large" />
                      <Text color="gray2" fontSize={4} lineHeight={5}>
                        {row.text}
                      </Text>
                      <Spacing size="large" />
                      <Grid columns={[1, 2]}>
                        {row.items?.map((item, index) => {
                          return (
                            item && (
                              <Cell key={index}>
                                <FlexItems
                                  alignItems="flex-start"
                                  gap={6}
                                  flexWrap="nowrap"
                                >
                                  {(item.icon || item.iconCustom) && (
                                    <FlexItem>
                                      <LandingPageIcon
                                        icon={item?.icon}
                                        iconCustom={item?.iconCustom}
                                        color={
                                          frontendTheme.LANDING_PAGES
                                            .STATS_FADING_BG.STAT_ICON_COLOR
                                        }
                                        height="20"
                                        width="20"
                                      />
                                    </FlexItem>
                                  )}
                                  <FlexItem>
                                    <Text
                                      color={
                                        frontendTheme.LANDING_PAGES
                                          .STATS_FADING_BG.STAT_TITLE_COLOR
                                      }
                                      tag="h3"
                                      fontWeight="bold"
                                      fontSize={[4, 5]}
                                    >
                                      {item.title}
                                    </Text>
                                    <Text
                                      color={
                                        frontendTheme.LANDING_PAGES
                                          .STATS_FADING_BG.STAT_TEXT_COLOR
                                      }
                                      marginTop={3}
                                    >
                                      {item.text}
                                    </Text>
                                  </FlexItem>
                                </FlexItems>
                              </Cell>
                            )
                          );
                        })}
                      </Grid>
                    </Main>
                  </Spacing>
                </Cell>
                <Cell display="grid" />
              </Grid>
            </Main>
          </div>
        </Spacing>
      </div>
    </LazyHydrate>
  );
};
export default StatsWithFadingBackground;
